import { Fragment, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import type {
  UserActionLogListingItem,
  UserActionPayloadTouchedEntity,
  UserActionsFilters,
} from '../types';
import { formatDateTime } from '../utils';
import { MethodBadge } from './MethodBadge';

interface Props {
  items: UserActionLogListingItem[];
  count: number;
  loading: boolean;
  error: string | null;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onFilterBySubject: (subjectType: string, subjectId: string) => void;
  onChangeFilters: (updater: (prev: UserActionsFilters) => UserActionsFilters) => void;
}

const COLUMN_COUNT = 9;

function subjectLabel(item: UserActionLogListingItem): string {
  const s = item.payload?.subject;
  if (!s) return '—';
  if (s.label) return s.label;
  return `${s.type}:${s.id}`;
}

export function ActionsTable({
  items,
  count,
  loading,
  error,
  page,
  limit,
  onPageChange,
  onLimitChange,
  onFilterBySubject,
  onChangeFilters,
}: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {error && (
        <Box sx={{ p: 1.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Когда</TableCell>
              <TableCell>Кто</TableCell>
              <TableCell>Проект</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Действие</TableCell>
              <TableCell>Метод</TableCell>
              <TableCell>Объект</TableCell>
              <TableCell>URL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Нет записей по выбранным фильтрам</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((it) => {
                const isOpen = !!expanded[it.id];
                const subj = it.payload?.subject;
                return (
                  <Fragment key={it.id}>
                    <TableRow
                      hover
                      sx={{ '& > td': { borderBottom: isOpen ? 'none' : undefined } }}
                    >
                      <TableCell>
                        <IconButton size="small" onClick={() => toggle(it.id)}>
                          {isOpen ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(it.created_at)}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0}>
                          <Typography variant="body2">{it.user_email ?? '—'}</Typography>
                          {!it.user_email && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {it.user_id}
                            </Typography>
                          )}
                          <Link
                            component="button"
                            type="button"
                            variant="caption"
                            onClick={() =>
                              onChangeFilters((prev) => ({ ...prev, user_id: it.user_id }))
                            }
                          >
                            фильтр
                          </Link>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {it.project_name ?? it.project_id ?? (
                          <Typography color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={it.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{it.action}</TableCell>
                      <TableCell>
                        <MethodBadge method={it.http_method} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title={subj ? `${subj.type}:${subj.id}` : ''}>
                            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                              {subjectLabel(it)}
                            </Typography>
                          </Tooltip>
                          {subj && (
                            <Tooltip title="Показать всё по этому объекту">
                              <IconButton
                                size="small"
                                onClick={() => onFilterBySubject(subj.type, subj.id)}
                              >
                                <FilterAltIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Tooltip title={it.url}>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ fontFamily: 'monospace', display: 'block' }}
                          >
                            {it.url}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={COLUMN_COUNT}
                        sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}
                      >
                        <Collapse in={isOpen} unmountOnExit>
                          <Box
                            sx={{
                              p: 2,
                              background: (t) => alpha(t.palette.primary.main, 0.03),
                            }}
                          >
                            <Stack spacing={1.5}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                  Полный URL
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                                >
                                  {it.url}
                                </Typography>
                              </Box>

                              {it.payload?.touched_entities &&
                                it.payload.touched_entities.length > 0 && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                      Затронутые сущности
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                      {it.payload.touched_entities.map(
                                        (te: UserActionPayloadTouchedEntity, idx) => (
                                          <Chip
                                            key={`${te.type}:${te.id}:${idx}`}
                                            size="small"
                                            label={`${te.action} → ${te.label ?? `${te.type}:${te.id}`}`}
                                            onClick={() => onFilterBySubject(te.type, te.id)}
                                            sx={{ cursor: 'pointer' }}
                                          />
                                        )
                                      )}
                                    </Stack>
                                  </Box>
                                )}

                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                  Payload
                                </Typography>
                                {it.payload ? (
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      p: 1.5,
                                      background: (t) => t.palette.background.default,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      fontSize: 12,
                                      fontFamily: 'monospace',
                                      maxHeight: 360,
                                      overflow: 'auto',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {JSON.stringify(it.payload, null, 2)}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Нет дополнительных данных
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        rowsPerPage={limit}
        onPageChange={(_, p) => onPageChange(p)}
        onRowsPerPageChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[20, 50, 100]}
        labelRowsPerPage="Записей на странице"
        labelDisplayedRows={({ from, to, count: c }) =>
          `${from}–${to} из ${c !== -1 ? c : `> ${to}`}`
        }
      />
    </Paper>
  );
}
